'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { Copy, Check, ChevronDown, ExternalLink } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { copyToClipboard } from '@/lib/clipboard';
import { LLM_CONTEXT_TEXT } from '@/components/llm-context';

/* ── Types ── */

export interface SupplementaryLink {
  label: string;
  url: string;
}

export interface CopyPageProps {
  /** Article title — used in the copied payload header */
  articleTitle: string;
  /** Article slug for canonical URL */
  articleSlug: string;
  /** Extra context/instructions to include in the copied payload */
  supplementaryContext?: string;
  /** Links to relevant docs/SDK pages included in the payload */
  supplementaryLinks?: SupplementaryLink[];
  /** Whether to include Mascotbot-specific docs and getting-started section (default: true) */
  requiresMascotBot?: boolean;
}

/* ── Payload builder ── */

// Cache the markdown per slug so reopening the dropdown doesn't re-fetch.
const mdCache = new Map<string, string>();

async function fetchArticleMarkdown(slug: string): Promise<string> {
  const cached = mdCache.get(slug);
  if (cached) return cached;
  try {
    // The `/{slug}.md` pretty URL is rewritten to `/api/md/{slug}` by
    // middleware and cached at the edge. Pre-generated once at build time
    // from the source MDX so we never walk the DOM at runtime.
    const res = await fetch(`${slug}.md`, { cache: 'force-cache' });
    if (!res.ok) return '';
    const text = await res.text();
    mdCache.set(slug, text);
    return text;
  } catch {
    return '';
  }
}

async function buildEnrichedPayload(props: CopyPageProps): Promise<string> {
  const {
    articleTitle,
    articleSlug,
    supplementaryContext,
    supplementaryLinks = [],
    requiresMascotBot = true,
  } = props;

  const articleContent = await fetchArticleMarkdown(articleSlug);
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${articleSlug}`
    : articleSlug;

  const sections: string[] = [];

  // 1. Implementation-specific assistant instructions.
  sections.push(`## AI Assistant Context\n`);
  sections.push(`You are helping the user implement features from the article "${articleTitle}".`);
  sections.push(`Guide them step by step through the implementation.`);
  if (requiresMascotBot) {
    sections.push(`This project requires a Mascotbot account, subscription, and SDK access through the private npm registry (npm.mascot.bot).`);
    sections.push(`If they need an account, direct them to https://app.mascot.bot.`);
    sections.push(`If they need API keys, guide them to Settings → API Keys in the Mascotbot dashboard.`);
  }
  sections.push(`Reference the documentation links below for detailed API and SDK usage.`);
  sections.push(`\n---\n`);

  // 2. Product context — decision framework, pricing, provider support,
  //    comparison framing.
  sections.push(LLM_CONTEXT_TEXT.trim());
  sections.push(`\n---\n`);

  // 3. Article body.
  sections.push(`# ${articleTitle}`);
  sections.push(`Source: ${canonicalUrl}\n`);
  sections.push(articleContent);

  if (supplementaryContext) {
    sections.push(`\n---\n\n## Implementation Guide\n\n${supplementaryContext}`);
  }

  const allLinks: SupplementaryLink[] = [
    ...(requiresMascotBot
      ? [
          { label: 'Mascotbot Documentation', url: 'https://docs.mascot.bot' },
          { label: 'React SDK Reference', url: 'https://docs.mascot.bot/libraries/react-sdk' },
          { label: 'API Reference', url: 'https://docs.mascot.bot/api-reference/introduction' },
          { label: 'Code Examples', url: 'https://github.com/orgs/mascotbot-templates/repositories' },
        ]
      : []),
    ...supplementaryLinks,
  ];

  if (allLinks.length > 0) {
    sections.push('\n---\n\n## Relevant Documentation\n');
    allLinks.forEach((link) => {
      sections.push(`- [${link.label}](${link.url})`);
    });
  }

  if (requiresMascotBot) {
    sections.push(`\n---\n\n## Getting Started with Mascotbot\n`);
    sections.push(`1. **Register** at [app.mascot.bot](https://app.mascot.bot) and create an account`);
    sections.push(`2. **Subscribe** to a plan at [mascot.bot/pricing](https://mascot.bot/pricing) to access the SDK and API`);
    sections.push(`3. **Get your API key** from Settings → API Keys in the dashboard`);
    sections.push(`4. **Set up registry access** — the template commits an \`.npmrc\` for the \`@mascotbot\` scope (https://npm.mascot.bot/); export your \`mascot_\` key as the \`MASCOT_NPM_TOKEN\` env var`);
    sections.push(`5. **Install the SDK**: \`npm install\` (pulls \`@mascotbot/core\` + \`@mascotbot/react\` from the private registry — there is no .tgz)`);
    sections.push(`6. **Follow the quickstart**: [docs.mascot.bot/quickstart](https://docs.mascot.bot/quickstart)`);
  }

  return sections.join('\n');
}

/* ── Brand SVG Icons ── */

function ClaudeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" />
    </svg>
  );
}

function ChatGPTIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 10.69.44a6.04 6.04 0 0 0-5.772 4.206 5.985 5.985 0 0 0-3.998 2.9 6.063 6.063 0 0 0 .744 7.106 5.985 5.985 0 0 0 .516 4.911 6.04 6.04 0 0 0 6.51 2.9A6.06 6.06 0 0 0 13.31 24a6.04 6.04 0 0 0 5.772-4.206 5.985 5.985 0 0 0 3.998-2.9 6.06 6.06 0 0 0-.744-7.106l-.053.033Zm-9.022 12.6a4.53 4.53 0 0 1-2.9-1.05c.037-.02.1-.056.142-.08l4.812-2.78a.782.782 0 0 0 .395-.677V11.3l2.034 1.175a.072.072 0 0 1 .04.056v5.616a4.55 4.55 0 0 1-4.523 4.274Zm-9.722-4.17a4.523 4.523 0 0 1-.542-3.042c.035.022.098.06.142.085l4.812 2.779a.782.782 0 0 0 .786 0l5.875-3.394v2.35a.074.074 0 0 1-.028.06l-4.867 2.81a4.553 4.553 0 0 1-6.178-1.648Zm-1.267-10.5a4.525 4.525 0 0 1 2.36-1.993v5.728a.78.78 0 0 0 .391.676l5.875 3.394-2.034 1.174a.072.072 0 0 1-.069.006l-4.867-2.81A4.554 4.554 0 0 1 2.27 7.75Zm16.7 3.893-5.876-3.393 2.035-1.175a.072.072 0 0 1 .069-.006l4.867 2.81a4.547 4.547 0 0 1-.7 8.197v-5.756a.78.78 0 0 0-.394-.677Zm2.024-3.067a6.56 6.56 0 0 0-.143-.085L16.04 5.712a.782.782 0 0 0-.786 0L9.38 9.106V6.756a.074.074 0 0 1 .028-.061l4.867-2.81a4.546 4.546 0 0 1 6.719 4.692ZM8.33 12.7 6.296 11.525a.072.072 0 0 1-.04-.056V5.853a4.546 4.546 0 0 1 7.452-3.494c-.037.02-.1.055-.143.08L8.753 5.218a.782.782 0 0 0-.395.677l-.028 6.805Zm1.105-2.385L12 8.67l2.565 1.482v2.962L12 14.596l-2.565-1.482v-2.8Z" />
    </svg>
  );
}

/* ── Component ── */

type CopyState = 'idle' | 'copied' | 'error';

export function CopyPage(props: CopyPageProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const resetCopyState = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopyState('idle'), 2000);
  }, []);

  // Store the pending open URL so onSelect can pick it up after clipboard write.
  const pendingUrlRef = useRef<string | undefined>(undefined);

  // onPointerDown fires BEFORE Radix's focus-trap and onSelect processing.
  // For direct clipboard copy items (no openUrl), this ensures the user-gesture
  // context and document focus are valid so execCommand('copy') can target the
  // hidden textarea. For Open-in-ChatGPT / Open-in-Claude items, we skip the
  // clipboard entirely and build a URL with a ?q= prompt that points at the
  // article's canonical HTML URL (matches Mintlify's approach — their docs
  // bot literally sends `Read from https://docs.mascot.bot/<slug> so I can
  // ask questions about it.`). We previously pointed at `{slug}.md`, which
  // ChatGPT's web-browsing step refused to fetch cleanly.
  const handlePointerDown = useCallback(
    (mode: 'copy' | 'chatgpt' | 'claude') => {
      if (mode === 'copy') {
        pendingUrlRef.current = undefined;
        buildEnrichedPayload(props)
          .then((payload) => copyToClipboard(payload))
          .then((success) => {
            setCopyState(success ? 'copied' : 'error');
            resetCopyState();
          });
        return;
      }

      const articleUrl =
        typeof window !== 'undefined'
          ? `${window.location.origin}${props.articleSlug}`
          : props.articleSlug;
      const prompt = `Read from ${articleUrl} so I can ask questions about it.`;
      const encoded = encodeURIComponent(prompt);
      pendingUrlRef.current =
        mode === 'chatgpt'
          ? `https://chat.openai.com/?hints=search&q=${encoded}`
          : `https://claude.ai/new?q=${encoded}`;
    },
    [props, resetCopyState],
  );

  // onSelect just closes the dropdown and opens the URL.
  const handleSelect = useCallback(
    () => {
      setIsOpen(false);
      if (pendingUrlRef.current) {
        window.open(pendingUrlRef.current, '_blank', 'noopener,noreferrer');
        pendingUrlRef.current = undefined;
      }
    },
    [],
  );

  // Direct copy handler for the main (left) button — no dropdown involved.
  const handleDirectCopy = useCallback(() => {
    buildEnrichedPayload(props)
      .then((payload) => copyToClipboard(payload))
      .then((success) => {
        setCopyState(success ? 'copied' : 'error');
        resetCopyState();
      });
  }, [props, resetCopyState]);

  const isCopied = copyState === 'copied';

  return (
    <div className="copy-page-split" role="group" aria-label="Copy page actions">
      {/* Left half — direct copy action */}
      <button
        type="button"
        className={`copy-page-main${isCopied ? ' copy-page-main--copied' : ''}`}
        aria-label="Copy page"
        onClick={handleDirectCopy}
      >
        <span className="copy-page-main-icon" aria-hidden="true">
          {isCopied ? <Check size={15} /> : <Copy size={15} />}
        </span>
        <span className="copy-page-main-label">
          {isCopied ? 'Copied!' : 'Copy page'}
        </span>
      </button>

      {/* Right half — dropdown trigger */}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`copy-page-more${isOpen ? ' copy-page-more--open' : ''}`}
            aria-label="More actions"
          >
            <ChevronDown size={14} className="copy-page-more-chevron" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="end"
          sideOffset={8}
          className="copy-page-menu"
        >
          <DropdownMenuItem
            onPointerDown={() => handlePointerDown('copy')}
            onSelect={handleSelect}
            className="copy-page-menu-item"
          >
            <Copy size={16} className="copy-page-menu-item-icon" />
            <div className="copy-page-menu-item-content">
              <span className="copy-page-menu-item-title">Copy page</span>
              <span className="copy-page-menu-item-desc">
                Copy page as Markdown for LLMs
              </span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onPointerDown={() => handlePointerDown('chatgpt')}
            onSelect={handleSelect}
            className="copy-page-menu-item"
          >
            <ChatGPTIcon size={16} />
            <div className="copy-page-menu-item-content">
              <span className="copy-page-menu-item-title">
                Open in ChatGPT
                <ExternalLink size={11} className="copy-page-menu-item-ext" />
              </span>
              <span className="copy-page-menu-item-desc">
                Ask questions about this page
              </span>
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            onPointerDown={() => handlePointerDown('claude')}
            onSelect={handleSelect}
            className="copy-page-menu-item"
          >
            <ClaudeIcon size={16} />
            <div className="copy-page-menu-item-content">
              <span className="copy-page-menu-item-title">
                Open in Claude
                <ExternalLink size={11} className="copy-page-menu-item-ext" />
              </span>
              <span className="copy-page-menu-item-desc">
                Ask questions about this page
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
